import { useEffect, useRef, useCallback } from 'react';
import { useRealtime, type ReservationNotificationPayload } from '../contexts/RealtimeContext';
import { useBooking } from '../contexts/BookingContext';

/**
 * Component that syncs real-time SignalR events with the BookingContext.
 * This component should be rendered inside both RealtimeProvider and BookingProvider.
 * It subscribes to facility/business groups and refreshes booking data on events.
 */
export const RealtimeBookingSync: React.FC = () => {
  const {
    isConnected,
    joinBusinessGroup,
    leaveBusinessGroup,
    onReservationCreated,
    onReservationCancelled,
    onReservationUpdated,
  } = useRealtime();

  const {
    businessProfile,
    selectedFacility,
    fetchFacilityBookings,
  } = useBooking();

  const currentBusinessIdRef = useRef<string | null>(null);

  // Join business group when connected and business profile is available
  useEffect(() => {
    if (!isConnected || !businessProfile?.id) {
      return;
    }

    const businessId = businessProfile.id;

    // Leave previous business group if different
    if (currentBusinessIdRef.current && currentBusinessIdRef.current !== businessId) {
      leaveBusinessGroup(currentBusinessIdRef.current).catch(console.error);
    }

    // Join new business group
    joinBusinessGroup(businessId).catch(console.error);
    currentBusinessIdRef.current = businessId;

    return () => {
      if (currentBusinessIdRef.current) {
        leaveBusinessGroup(currentBusinessIdRef.current).catch(console.error);
        currentBusinessIdRef.current = null;
      }
    };
  }, [isConnected, businessProfile?.id, joinBusinessGroup, leaveBusinessGroup]);

  // Handle reservation events
  const handleReservationEvent = useCallback(
    (payload: ReservationNotificationPayload) => {
      console.log('[RealtimeBookingSync] Reservation event received:', payload);

      // Check if this event is for the currently selected facility
      if (selectedFacility && payload.facilityId === selectedFacility.id) {
        // Refresh the facility bookings for the affected date
        console.log(
          `[RealtimeBookingSync] Refreshing bookings for facility ${payload.facilityId}, date ${payload.date}`
        );
        fetchFacilityBookings(payload.facilityId, payload.date);
      }
    },
    [selectedFacility, fetchFacilityBookings]
  );

  // Subscribe to reservation events
  useEffect(() => {
    const unsubscribeCreated = onReservationCreated(handleReservationEvent);
    const unsubscribeCancelled = onReservationCancelled(handleReservationEvent);
    const unsubscribeUpdated = onReservationUpdated(handleReservationEvent);

    return () => {
      unsubscribeCreated();
      unsubscribeCancelled();
      unsubscribeUpdated();
    };
  }, [onReservationCreated, onReservationCancelled, onReservationUpdated, handleReservationEvent]);

  // This component doesn't render anything visible
  return null;
};
